import React, { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "./components/Sidebar";
import API_BASE_URL from "./config";
import "./AdBanners.css";

// The visitor-facing slider shows banners on a 5:1 canvas (see ClientGallery.css).
// Recommending the exact ratio means uploads fill the frame edge-to-edge with no
// letterboxing — but any size still displays in full, never cropped.
const RECO_W = 1200;
const RECO_H = 240;

function AdBanners() {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [banners, setBanners] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const inputRef = useRef(null);

  const userId = localStorage.getItem("userId");

  const fetchBanners = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/studio/${userId}/banners`);
      const data = await res.json();
      setBanners(data.banners || []);
    } catch {}
  }, [userId]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  useEffect(() => {
    if (message.text) {
      const t = setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const uploadFiles = useCallback(
    async (files) => {
      const images = files.filter((f) => f.type.startsWith("image/"));
      if (!images.length) {
        setMessage({ text: "Please choose image files only", type: "error" });
        return;
      }
      setUploading(true);
      try {
        for (const file of images) {
          const imageData = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(file);
          });
          await fetch(`${API_BASE_URL}/api/studio/${userId}/banners`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ imageData }),
          });
        }
        await fetchBanners();
        setMessage({
          text: `${images.length} banner${images.length > 1 ? "s" : ""} uploaded`,
          type: "success",
        });
      } catch {
        setMessage({ text: "Failed to upload banner", type: "error" });
      } finally {
        setUploading(false);
      }
    },
    [userId, fetchBanners]
  );

  const handleInputChange = async (e) => {
    await uploadFiles(Array.from(e.target.files));
    e.target.value = "";
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragActive(false);
    if (uploading) return;
    await uploadFiles(Array.from(e.dataTransfer.files || []));
  };

  const handleDelete = async (bannerId) => {
    try {
      await fetch(`${API_BASE_URL}/api/studio/${userId}/banners/${bannerId}`, { method: "DELETE" });
      await fetchBanners();
      setMessage({ text: "Banner deleted", type: "success" });
    } catch {
      setMessage({ text: "Failed to delete banner", type: "error" });
    }
  };

  return (
    <div className="ab-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="ab-main">
        <header className="ab-header">
          <div className="ab-header-left">
            <button className="ab-toggle-sidebar" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <div>
              <h1 className="ab-page-title">My Ad Banners</h1>
              <p className="ab-page-sub">Promotional banners shown across your shared galleries</p>
            </div>
          </div>
          <span className="ab-count-pill">{banners.length} active</span>
        </header>

        <main className="ab-content">
          {message.text && (
            <div className={`ab-message ab-message--${message.type}`}>{message.text}</div>
          )}

          {/* Upload dropzone */}
          <div
            className={`ab-dropzone ${dragActive ? "is-drag" : ""} ${uploading ? "is-busy" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              if (!uploading) setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            onClick={() => !uploading && inputRef.current?.click()}
            role="button"
            tabIndex={0}
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleInputChange}
              disabled={uploading}
              hidden
            />
            <div className="ab-dropzone-icon">{uploading ? "⏳" : "⬆️"}</div>
            <div className="ab-dropzone-title">
              {uploading ? "Uploading…" : "Drag & drop banners here"}
            </div>
            <div className="ab-dropzone-sub">
              {uploading ? "Please wait" : "or click to browse — you can add several at once"}
            </div>

            <div className="ab-specs">
              <span className="ab-spec">
                <strong>Recommended</strong> {RECO_W} × {RECO_H} px
              </span>
              <span className="ab-spec">
                <strong>Shape</strong> wide 5:1 strip
              </span>
              <span className="ab-spec">
                <strong>Format</strong> JPG or PNG
              </span>
            </div>
          </div>

          <p className="ab-hint">
            Banners auto-rotate at the top of <strong>every shared gallery</strong> your studio creates.
            Any image works — it's always shown in full, never cropped — but the {RECO_W} × {RECO_H} px
            size fills the frame edge-to-edge for the sharpest result. No banners? Photentra's defaults appear instead.
          </p>

          {/* Banner list */}
          {banners.length === 0 ? (
            <div className="ab-empty">
              <div className="ab-empty-icon">🖼️</div>
              <h3>No banners yet</h3>
              <p>Upload a wide banner above to start advertising on your shared gallery pages.</p>
            </div>
          ) : (
            <section className="ab-list-section">
              <h2 className="ab-list-title">Your banners ({banners.length})</h2>
              <p className="ab-list-note">This is exactly how each banner appears to visitors.</p>

              <div className="ab-list">
                {banners.map((banner, idx) => (
                  <div key={banner.id} className="ab-item">
                    <span className="ab-item-order">{idx + 1}</span>
                    <div className="ab-preview">
                      <div
                        className="ab-preview-bg"
                        style={{ backgroundImage: `url(${banner.image_url})` }}
                        aria-hidden="true"
                      />
                      <img src={banner.image_url} alt={`Banner ${idx + 1}`} className="ab-preview-img" />
                    </div>
                    <button
                      className="ab-item-delete"
                      onClick={() => handleDelete(banner.id)}
                      title="Delete banner"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}

export default AdBanners;
